import React, { useEffect, useState, useRef } from "react";
import Modal from "react-modal";
import { CiFileOn } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
// import * as XLSX from "xlsx";
import { uploadScoutingReport, updateReportAll } from "../../app/features/scoutingReports/reportAction";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../app/features/user/userSlice";
import {
  addReport,
  selectReport,
  resetUploadState,
} from "../../app/features/scoutingReports/reportSlice";

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
  },
};

// Make sure to bind modal to your appElement (https://reactcommunity.org/react-modal/accessibility/)
Modal.setAppElement("#root");

function UploadReports({ modalIsOpen, setIsOpen, openModal }) {
  const [file, setFile] = React.useState(null);
  const [progress, setProgress] = React.useState(0);
  const [uploading, setUploading] = React.useState(false);
  const dispatch = useDispatch();
  const { userToken } = useSelector(selectUser);
  const navigate = useNavigate();
  const { loading, success, reports } = useSelector(selectReport);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const abortControllerRef = useRef(null);

  // const flattenTopLevelArrays = (obj) => {
  //   return Object.values(obj).flat();
  // };

  function closeModal() {
    // Cancel any ongoing API request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Reset the upload state
    dispatch(resetUploadState());

    setIsOpen(false);
    setFile(null);
    setProgress(0);
    setUploading(false);
  }

  async function handleFileChange(e) {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setProgress(0);
      setUploading(true);
      setFile(selectedFile);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
      setProgress(0);
      setUploading(true);
    }
  }

  function handleDragOver(e) {
    e.preventDefault();
  }

  function handleRemoveFile() {
    setFile(null);
    setProgress(0);
    setUploading(false);
  }

  // Simulate upload progress
  useEffect(() => {
    let timer;
    if (uploading && file && progress < 100) {
      timer = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(timer);
            setUploading(false);
            return 100;
          }
          return prev + 10;
        });
      }, 200);
    }
    return () => clearInterval(timer);
  }, [uploading, file, progress]);

  useEffect(() => {
    if (success && reports) {
      setConfirmationModal(true);
    }
  }, [success]);

  // Cleanup effect to abort any ongoing requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      // Reset upload state on unmount
      dispatch(resetUploadState());
    };
  }, [dispatch]);

  const handleNext = () => {
    // Create a new AbortController for this request
    abortControllerRef.current = new AbortController();

    dispatch(
      uploadScoutingReport({
        scoutingReport: file,
        token: userToken?.access,
        signal: abortControllerRef.current.signal
      })
    );
    // console.log("clicked");
  };

  return (
    <div>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={customStyles}
        contentLabel="Load board with scouting report"
        shouldCloseOnOverlayClick={true}
      >
        <div
          style={{
            position: "relative",
            padding: 0,
            minWidth: 760,
            minHeight: 350,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          {/* Close Icon */}
          <button
            onClick={closeModal}
            style={{
              position: "absolute",
              top: 0,
              right: -5,
              background: "none",
              border: "1px solid black",
              borderRadius: "50%",
              padding: "0px 5px",
              fontSize: 20,
              cursor: "pointer",
            }}
            aria-label="Close"
          >
            ×
          </button>
          {/* Title */}
          <div>
            <h2
              style={{
                textAlign: "left",
                fontWeight: 600,
                fontSize: 22,
                margin: "10px 0 24px 32px",
              }}
            >
              Load board with scouting report
            </h2>
            {/* Drag & Drop Area */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              style={{
                border: "2px dashed #B6C2CD",
                borderRadius: 8,
                background: "#F8FAFB",
                height: 316,
                width: 880,
                margin: "0 32px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              {/* File Icon */}
              <div style={{ fontSize: 48, color: "#B6C2CD", marginBottom: 12 }}>
                <CiFileOn />
              </div>
              <div style={{ fontSize: 16, color: "#6B7A90", marginBottom: 8 }}>
                Drag and drop your file here
              </div>
              <label
                htmlFor="file-upload"
                style={{
                  color: "#1A73E8",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontSize: 16,
                }}
              >
                {file ? file.name : "Choose file"}
                <input
                  id="file-upload"
                  type="file"
                  accept=".xls,.xlsx"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </label>
            </div>
            {/* File info and progress bar */}
            {file && (
              <div
                style={{
                  margin: "24px 32px 0 32px",
                  background: "#F8FAFB",
                  borderRadius: 6,
                  border: "1px solid #E3E8EF",
                  padding: "12px 16px 8px 16px",
                  width: 880,
                  position: "relative",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <div
                    style={{ fontSize: 20, color: "#6B7A90", marginRight: 12 }}
                  >
                    <CiFileOn />
                  </div>
                  <div
                    style={{
                      flex: 1,
                      fontWeight: 500,
                      color: "#222",
                      fontSize: 16,
                    }}
                  >
                    {file.name}
                  </div>
                  <div
                    style={{ color: "#6B7A90", fontSize: 14, marginRight: 16 }}
                  >
                    {`${(file.size / (1024 * 1024)).toFixed(1)} MB`}
                  </div>
                  <button
                    onClick={handleRemoveFile}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#6B7A90",
                      fontSize: 20,
                      cursor: "pointer",
                    }}
                    aria-label="Remove file"
                  >
                    ×
                  </button>
                </div>
                {/* Progress Bar: only show if progress < 100 */}
                {progress < 100 && (
                  <div
                    style={{
                      height: 6,
                      background: "#E3E8EF",
                      borderRadius: 3,
                      width: "100%",
                      marginBottom: 4,
                    }}
                  >
                    <div
                      style={{
                        width: `${progress}%`,
                        height: "100%",
                        background: "#1A73E8",
                        borderRadius: 3,
                        transition: "width 0.2s",
                      }}
                    ></div>
                  </div>
                )}
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                margin: "0 32px",
                width: "90%",
                margin: "auto",
              }}
            >
              <div
                style={{
                  color: "#6B7A90",
                }}
              >
                Supported format: XLS
              </div>
              <div
                style={{
                  color: "#6B7A90",
                }}
              >
                Maximum size: 20MB
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 32,
              marginRight: 32,
            }}
          >
            <button
              onClick={closeModal}
              style={{
                background: "#fff",
                border: "1px solid #B6C2CD",
                borderRadius: 4,
                padding: "8px 24px",
                fontWeight: 500,
                fontSize: 16,
                color: "#6B7A90",
                marginRight: 12,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleNext}
              disabled={!file || progress < 100 || loading}
              style={{
                background: file && progress === 100 && !loading ? "#1A73E8" : "#E3E8EF",
                color: file && progress === 100 && !loading ? "#fff" : "#B6C2CD",
                border: "none",
                borderRadius: 4,
                padding: "8px 24px",
                fontWeight: 500,
                fontSize: 16,
                cursor: file && progress === 100 && !loading ? "pointer" : "not-allowed",
              }}
            >
              {loading ? "Uploading..." : "Next"}
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={confirmationModal}
        onRequestClose={() => setConfirmationModal(false)}
        style={customStyles}
        contentLabel="Confirmation Modal"
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: 32,
            minWidth: 400,
            borderRadius: 16,
            background: "#fff",
            boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
          }}
        >
          {/* Success Icon */}
          <div
            style={{
              background: "#E6F4EA",
              borderRadius: "50%",
              width: 64,
              height: 64,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 24,
            }}
          >
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="12" fill="#34A853" fillOpacity="0.15"/>
              <path d="M7 13.5L10.5 17L17 10.5" stroke="#34A853" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          {/* Headline */}
          <h2 style={{ fontWeight: 700, fontSize: 24, margin: 0, color: "#222", marginBottom: 12 }}>Upload Successful</h2>
          {/* Message */}
          <div style={{ textAlign: "center", color: "#555", fontSize: 16, marginBottom: 28 }}>
            <p style={{ margin: 0 }}>
              Successfully uploaded <span style={{ fontWeight: 600 }}>{reports.length}</span> players.<br />
              What would you like to do next?
            </p>
          </div>
          {/* Action Buttons */}
          <div style={{ display: "flex", gap: 16, width: "100%", justifyContent: "center" }}>
            <button
              onClick={() => {
                dispatch(
                  updateReportAll({
                    ids: reports.map((report) => report.id),
                    token: userToken?.access,
                  })
                );
                setConfirmationModal(false);
              }}
              style={{
                background: "#1A73E8",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "10px 28px",
                fontWeight: 600,
                fontSize: 16,
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(26,115,232,0.08)",
                transition: "background 0.2s",
              }}
              onMouseOver={e => e.currentTarget.style.background = '#1765c1'}
              onMouseOut={e => e.currentTarget.style.background = '#1A73E8'}
            >
              Verify All Reports
            </button>
            <button
              onClick={() => {
                navigate(`/scouting-reports/edit/${reports[0]?.id}`, {
                  state: { singleReport: false },
                });
                setConfirmationModal(false);
              }}
              style={{
                background: "#fff",
                color: "#1A73E8",
                border: "2px solid #1A73E8",
                borderRadius: 6,
                padding: "10px 28px",
                fontWeight: 600,
                fontSize: 16,
                cursor: "pointer",
                transition: "background 0.2s, color 0.2s",
              }}
              onMouseOver={e => {
                e.currentTarget.style.background = '#E8F0FE';
                e.currentTarget.style.color = '#1765c1';
              }}
              onMouseOut={e => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.color = '#1A73E8';
              }}
            >
              Edit All Reports
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default UploadReports;
